// CRITICAL: Import dayjs configuration FIRST before any other imports
import "utils/dayjs";

import { LoadingOutlined } from "@ant-design/icons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import {
  App as AntdApp,
  ConfigProvider as AntdConfigProvider,
} from "antd";
import mnMN from "antd/lib/locale/mn_MN";
import { StoreProvider, useStore } from "contexts";
import { useAtomValue } from "jotai";
import { FC, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { createRoot } from "react-dom/client";
import { router } from "routes/router";
import { themeModeAtom } from "theme/mode";
import { getThemeConfig } from "theme/tokens";
import "./global.less";
import "./tailwind.css";
import "./styles/index.css";

const queryClient = new QueryClient();

const InnerApp: FC = () => {
  const [{ authorized }] = useStore();

  return (
    <RouterProvider
      router={router}
      context={{ authorized }}
    />
  );
};

const App = () => {
  const mode = useAtomValue(themeModeAtom);

  // Expose the mode on <html> so custom less/tailwind can react to dark mode.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  return (
    <AntdConfigProvider
      popupMatchSelectWidth={false}
      spin={{
        indicator: <LoadingOutlined spin />,
      }}
      theme={getThemeConfig(mode)}
      locale={mnMN}
    >
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <StoreProvider>
            <DndProvider backend={HTML5Backend}>
              <InnerApp />
            </DndProvider>
          </StoreProvider>
        </QueryClientProvider>
      </AntdApp>
    </AntdConfigProvider>
  );
};

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = createRoot(container);
root.render(<App />);
